import React from 'react';
import PaymentsReportsService from '../../services/api/payments-reports.service';
import EditableTable from '../../common/EditableTable';
import { paymentColumns, refundColumns } from '../../config/table-columns/pagamenti-rimborsi';
import {
    IPaymentContactRow,
    IPaymentReport,
    IPaymentReportChanges,
    IReportChangesSectionResult,
    IReportRowChanges,
    IPaymentRow,
    IRefundRow,
} from '../../helpers/interfaces/payments-reports';
import Swal from "sweetalert2";
import EventBus from '../../common/EventBus';

type Props = {}

type State = {
    reports: IPaymentReport[];
    activeReport: IPaymentReport | null;
    activeTab: 'payments' | 'contacts' | 'refunds';
    paymentRows: IPaymentRow[];
    paymentContactRows: IPaymentContactRow[];
    refundRows: IRefundRow[];
    savedPaymentRows: IPaymentRow[];
    savedContactRows: IPaymentContactRow[];
    savedRefundRows: IRefundRow[];
    hasChanges: boolean;
    isSaving: boolean;
    saveStatus: 'idle' | 'pending' | 'saving' | 'saved' | 'error';
    userRoles: string[];
    userActions: string[];
    newMonth: number;
    newYear: number;
    reportPickerOpen: boolean;
    reportSearch: string;
    reportStatusFilter: 'all' | 'draft' | 'validated';
    reportPage: number;
    reportLimit: number;
    reportTotal: number;
    reportPages: number;
    contactSearch: string;
    contactFilter: 'all' | 'incomplete';
    contactPage: number;
    contactLimit: number;
}

const paymentContactColumnKeys = ['email', 'phone', 'firstName', 'lastName', 'paymentDate'];

export default class PagamentiRimborsi extends React.Component<Props, State> {
    private rowSequence = 0;
    private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
    private savedStatusTimer: ReturnType<typeof setTimeout> | null = null;
    private saveInProgress = false;
    private autoSaveQueued = false;

    constructor(props: Props) {
        super(props);
        const now = new Date();
        this.state = {
            reports: [],
            activeReport: null,
            activeTab: 'payments',
            paymentRows: [],
            paymentContactRows: [],
            refundRows: [],
            savedPaymentRows: [],
            savedContactRows: [],
            savedRefundRows: [],
            hasChanges: false,
            isSaving: false,
            saveStatus: 'idle',
            userRoles: [],
            userActions: [],
            newMonth: now.getMonth() + 1,
            newYear: now.getFullYear(),
            reportPickerOpen: false,
            reportSearch: '',
            reportStatusFilter: 'all',
            reportPage: 1,
            reportLimit: 50,
            reportTotal: 0,
            reportPages: 1,
            contactSearch: '',
            contactFilter: 'all',
            contactPage: 1,
            contactLimit: 50,
        };
    }

    async componentDidMount(): Promise<void> {
        EventBus.dispatch("showLoader", { text: 'Caricamento report in corso...' });

        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const user = JSON.parse(userStr);
                this.setState({
                    userRoles: (user.roles || []).map((r: any) => r.code?.toUpperCase()),
                    userActions: (user.actions || [])
                        .map((action: any) => typeof action === 'string' ? action : action.code)
                        .filter((code: any) => !!code)
                        .map((code: string) => code.toUpperCase()),
                });
            }
        } catch (e) { /* ignore */ }

        try {
            await this.loadReports();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare i report.' });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    }

    componentWillUnmount(): void {
        this.clearAutoSaveTimer();
        if (this.savedStatusTimer) {
            clearTimeout(this.savedStatusTimer);
        }
    }

    loadReports = async (page: number = this.state.reportPage) => {
        const { reportLimit, reportSearch, reportStatusFilter } = this.state;
        const response = await PaymentsReportsService.getReports(page, reportLimit, reportSearch, reportStatusFilter);
        this.setState({
            reports: response.rows,
            reportTotal: response.total,
            reportPage: response.page,
            reportLimit: response.limit,
            reportPages: response.pages || 1,
        });
    };

    canEdit = (): boolean => {
        const { userActions, userRoles } = this.state;
        return userRoles.includes('ADM') || userActions.includes('PED');
    };

    canValidate = (): boolean => {
        return this.canEdit();
    };

    canExport = (): boolean => {
        return true;
    };

    canDelete = (): boolean => {
        const { userActions, userRoles } = this.state;
        return userRoles.includes('ADM') || userActions.includes('PDE');
    };

    isReadOnly = (): boolean => {
        if (!this.state.activeReport) return true;
        if (this.state.activeReport.status === 'validated') return true;
        return !this.canEdit();
    };

    getApiErrorMessage = (error: any, fallback: string): string => {
        const apiError = error?.response?.data?.error;
        const description = apiError?.description;
        const message = apiError?.message;

        if (typeof description === 'string' && description.trim() !== '') {
            return description;
        }

        if (typeof message === 'string' && message.trim() !== '') {
            return message;
        }

        if (message && typeof message === 'object') {
            const firstMessage = Object.values(message).find(value => typeof value === 'string' && value.trim() !== '');
            if (typeof firstMessage === 'string') {
                return firstMessage;
            }
        }

        return fallback;
    };

    hasPaymentData = (row: IPaymentRow): boolean => {
        const values = [
            row.vendor, row.orderConfirmationNumber, row.grossAmount, row.netAmount,
            row.recipient, row.date, row.part, row.monthProgressive,
            row.paymentVerification, row.brand, row.genre, row.source,
            row.affiliate, row.demolition, row.link, row.total,
        ];

        return values.some(value => value !== null && value !== undefined && String(value).trim() !== '');
    };

    hasPaymentContactData = (row: IPaymentContactRow): boolean => {
        return [row.email, row.phone, row.firstName, row.lastName, row.paymentDate]
            .some(value => value !== null && value !== undefined && String(value).trim() !== '');
    };

    isValidEmail = (email: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    hasRefundData = (row: IRefundRow): boolean => {
        const values = [
            row.vendor, row.creditNoteNumber, row.orderConfirmationNumber, row.amount,
            row.recipient, row.date, row.part, row.monthProgressive,
            row.brand, row.genre, row.legalReview, row.total,
        ];

        return values.some(value => value !== null && value !== undefined && String(value).trim() !== '');
    };

    createClientId = (prefix: string): string => {
        const cryptoApi = typeof window !== 'undefined' ? (window.crypto as any) : undefined;
        if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
            return prefix + '-' + cryptoApi.randomUUID();
        }

        this.rowSequence += 1;
        return prefix + '-' + Date.now().toString(36) + '-' +
            Math.random().toString(36).slice(2) + '-' + this.rowSequence;
    };

    normalizeClientIds = <T extends { id?: number | null; clientId?: string }>(rows: T[], prefix: string): T[] => {
        let nextSortOrder = rows.reduce(
            (maximum, row: any) => row.id || row.clientId
                ? Math.max(maximum, Number(row.sortOrder) || 0)
                : maximum,
            -1
        ) + 1;

        return rows.map(row => {
            if (row.id || row.clientId) return row;
            return {
                ...row,
                clientId: this.createClientId(prefix),
                sortOrder: nextSortOrder++,
            };
        });
    };

    comparableRow = (row: any): string => {
        const { clientId, ...data } = row;
        return JSON.stringify(data);
    };

    buildRowChanges = <T extends { id?: number | null; clientId?: string }>(
        currentRows: T[],
        savedRows: T[],
        hasData: (row: T) => boolean
    ): IReportRowChanges<T> => {
        const meaningfulRows = currentRows.filter(hasData);
        const currentById = new Map<number, T>(
            meaningfulRows.filter(row => !!row.id).map(row => [row.id as number, row])
        );
        const savedById = new Map<number, T>(
            savedRows.filter(row => !!row.id).map(row => [row.id as number, row])
        );

        return {
            created: meaningfulRows.filter(row => !row.id),
            updated: meaningfulRows.filter(row => {
                if (!row.id) return false;
                const saved = savedById.get(row.id);
                return !!saved && this.comparableRow(row) !== this.comparableRow(saved);
            }),
            deleted: savedRows
                .filter(row => !!row.id && !currentById.has(row.id as number))
                .map(row => row.id as number),
        };
    };

    buildReportChanges = (): IPaymentReportChanges | null => {
        if (!this.state.activeReport) return null;

        return {
            version: this.state.activeReport.version,
            payments: this.buildRowChanges(this.state.paymentRows, this.state.savedPaymentRows, this.hasPaymentData),
            contacts: this.buildRowChanges(this.state.paymentContactRows, this.state.savedContactRows, this.hasPaymentContactData),
            refunds: this.buildRowChanges(this.state.refundRows, this.state.savedRefundRows, this.hasRefundData),
        };
    };

    hasPersistableChanges = (changes: IPaymentReportChanges): boolean => {
        return [changes.payments, changes.contacts, changes.refunds]
            .some(section => section.created.length > 0 || section.updated.length > 0 || section.deleted.length > 0);
    };

    clearAutoSaveTimer = () => {
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = null;
        }
    };

    scheduleAutoSave = () => {
        this.clearAutoSaveTimer();
        if (this.savedStatusTimer) {
            clearTimeout(this.savedStatusTimer);
            this.savedStatusTimer = null;
        }

        const changes = this.buildReportChanges();
        if (!changes || this.isReadOnly() || !this.hasPersistableChanges(changes)) {
            this.setState({ hasChanges: false, saveStatus: 'idle' });
            return;
        }

        this.setState({ saveStatus: 'pending' });
        this.autoSaveTimer = setTimeout(() => {
            this.autoSaveTimer = null;
            if (this.saveInProgress) {
                this.autoSaveQueued = true;
                return;
            }
            void this.saveReport(true);
        }, 800);
    };

    scheduleSavedStatusReset = () => {
        if (this.savedStatusTimer) {
            clearTimeout(this.savedStatusTimer);
        }
        this.savedStatusTimer = setTimeout(() => {
            this.savedStatusTimer = null;
            if (!this.state.hasChanges && !this.state.isSaving) {
                this.setState({ saveStatus: 'idle' });
            }
        }, 2000);
    };

    applyChangesResult = <T extends { id?: number | null; clientId?: string }>(
        rows: T[],
        result: IReportChangesSectionResult<T>,
        hasData: (row: T) => boolean
    ): T[] => {
        const createdByClientId = new Map(result.created.map(item => [item.clientId, item.row]));
        const updatedById = new Map(result.updated.map(row => [row.id as number, row]));
        const deletedIds = new Set(result.deleted);

        return rows
            .filter(hasData)
            .filter(row => !row.id || !deletedIds.has(row.id))
            .map(row => {
                if (!row.id && row.clientId && createdByClientId.has(row.clientId)) {
                    return createdByClientId.get(row.clientId) as T;
                }
                if (row.id && updatedById.has(row.id)) {
                    return updatedById.get(row.id) as T;
                }
                return row;
            });
    };

    isContactIncomplete = (row: IPaymentContactRow): boolean => {
        return [row.email, row.phone, row.firstName, row.lastName, row.paymentDate]
            .some(value => !value || String(value).trim() === '');
    };

    getFilteredContactRows = (): IPaymentContactRow[] => {
        const search = this.state.contactSearch.trim().toLowerCase();

        return this.state.paymentContactRows.filter(row => {
            if (this.state.contactFilter === 'incomplete' && !this.isContactIncomplete(row)) {
                return false;
            }

            if (!search) return true;

            return [row.email, row.phone, row.firstName, row.lastName, row.paymentDate]
                .some(value => String(value || '').toLowerCase().includes(search));
        });
    };

    getVisibleContactRows = (): IPaymentContactRow[] => {
        const start = (this.state.contactPage - 1) * this.state.contactLimit;
        return this.getFilteredContactRows().slice(start, start + this.state.contactLimit);
    };

    addPaymentContactRow = () => {
        const nextSortOrder = this.state.paymentContactRows.reduce(
            (maximum, row) => Math.max(maximum, Number(row.sortOrder) || 0),
            -1
        ) + 1;
        const paymentContactRows = [
            ...this.state.paymentContactRows,
            {
                clientId: this.createClientId('contact'),
                email: '',
                phone: '',
                firstName: '',
                lastName: '',
                paymentDate: '',
                sortOrder: nextSortOrder,
            },
        ];
        const contactPage = Math.max(1, Math.ceil(paymentContactRows.length / this.state.contactLimit));

        this.setState({
            paymentContactRows,
            contactSearch: '',
            contactFilter: 'all',
            contactPage,
            hasChanges: true,
        }, this.scheduleAutoSave);
    };

    selectReport = async (id: number) => {
        this.clearAutoSaveTimer();
        EventBus.dispatch("showLoader", { text: 'Caricamento report...' });
        try {
            const report = await PaymentsReportsService.getReport(id);
            const paymentRows = report.paymentRows || [];
            const contactRows = report.contactRows || [];
            const refundRows = report.refundRows || [];
            this.setState({
                activeReport: report,
                paymentRows,
                paymentContactRows: contactRows,
                refundRows,
                savedPaymentRows: paymentRows,
                savedContactRows: contactRows,
                savedRefundRows: refundRows,
                hasChanges: false,
                saveStatus: 'idle',
                activeTab: 'payments',
                reportPickerOpen: false,
                contactSearch: '',
                contactFilter: 'all',
                contactPage: 1,
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare il report.' });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    };

    requestReportChange = async (id: number) => {
        if (this.state.activeReport?.id === id) {
            this.setState({ reportPickerOpen: false });
            return;
        }

        const canContinue = await this.confirmPendingChanges(
            'Salva e cambia report',
            'Prima di cambiare report, scegli come gestire le modifiche correnti.'
        );
        if (!canContinue) return;

        await this.selectReport(id);
    };

    confirmPendingChanges = async (confirmButtonText: string, text: string): Promise<boolean> => {
        if (!this.state.hasChanges) return true;

        const result = await Swal.fire({
            icon: 'warning',
            title: 'Modifiche non salvate',
            text,
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText,
            denyButtonText: 'Scarta modifiche',
            cancelButtonText: 'Annulla',
        });

        if (result.isConfirmed) {
            return this.saveReport();
        }

        return result.isDenied;
    };

    createReport = async () => {
        const canContinue = await this.confirmPendingChanges(
            'Salva e crea report',
            'Prima di creare il report, scegli come gestire le modifiche correnti.'
        );
        if (!canContinue) return;

        this.clearAutoSaveTimer();
        const { newMonth, newYear } = this.state;
        EventBus.dispatch("showLoader", { text: 'Creazione report...' });
        try {
            const report = await PaymentsReportsService.createReport(newMonth, newYear);
            await this.loadReports(1);
            this.setState({
                activeReport: report,
                paymentRows: [],
                paymentContactRows: [],
                refundRows: [],
                savedPaymentRows: [],
                savedContactRows: [],
                savedRefundRows: [],
                hasChanges: false,
                saveStatus: 'idle',
                activeTab: 'payments',
                reportPickerOpen: false,
                contactSearch: '',
                contactFilter: 'all',
                contactPage: 1,
            });
            Swal.fire({ icon: 'success', title: 'Report creato', timer: 1500, showConfirmButton: false });
        } catch (error: any) {
            const fallback = 'Impossibile creare il report per ' + this.getMonthName(newMonth) + ' ' + newYear + '.';
            const msg = this.getApiErrorMessage(error, fallback);
            Swal.fire({ icon: 'error', title: 'Errore', text: msg });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    };

    handlePaymentRowsChange = (rows: IPaymentRow[]) => {
        this.setState(
            { paymentRows: this.normalizeClientIds(rows, 'payment'), hasChanges: true },
            this.scheduleAutoSave
        );
    };

    handlePaymentContactRowsChange = (rows: IPaymentContactRow[]) => {
        const previousVisibleRows = this.getVisibleContactRows();
        const previousKeys = new Set(
            previousVisibleRows
                .map(row => row.id ? 'contact-' + row.id : row.clientId)
                .filter((key): key is string => !!key)
        );
        const normalizedRows: IPaymentContactRow[] = rows.map(row => ({
            ...row,
            clientId: row.id ? row.clientId : (row.clientId || this.createClientId('contact')),
        }));
        const getKey = (row: IPaymentContactRow): string => row.id ? 'contact-' + row.id : (row.clientId as string);
        const nextRowsByKey = new Map<string, IPaymentContactRow>(normalizedRows.map(row => [getKey(row), row]));
        const nextKeys = new Set(normalizedRows.map(getKey));

        const paymentContactRows = this.state.paymentContactRows
            .filter(row => !previousKeys.has(getKey(row)) || nextKeys.has(getKey(row)))
            .map(row => nextRowsByKey.get(getKey(row)) || row);

        normalizedRows.forEach(row => {
            if (!previousKeys.has(getKey(row))) {
                paymentContactRows.push(row);
            }
        });

        const addedRows = normalizedRows.some(row => !previousKeys.has(getKey(row)));
        const nextState: Pick<State, 'paymentContactRows' | 'hasChanges' | 'contactPage' | 'contactSearch' | 'contactFilter'> = {
            paymentContactRows,
            hasChanges: true,
            contactPage: this.state.contactPage,
            contactSearch: this.state.contactSearch,
            contactFilter: this.state.contactFilter,
        };

        if (addedRows) {
            nextState.contactSearch = '';
            nextState.contactFilter = 'all';
            nextState.contactPage = Math.max(1, Math.ceil(paymentContactRows.length / this.state.contactLimit));
        } else {
            const filteredCount = paymentContactRows.filter(row => {
                if (nextState.contactFilter === 'incomplete' && !this.isContactIncomplete(row)) return false;
                if (!nextState.contactSearch.trim()) return true;
                const search = nextState.contactSearch.trim().toLowerCase();
                return [row.email, row.phone, row.firstName, row.lastName, row.paymentDate]
                    .some(value => String(value || '').toLowerCase().includes(search));
            }).length;
            nextState.contactPage = Math.min(
                nextState.contactPage,
                Math.max(1, Math.ceil(filteredCount / this.state.contactLimit))
            );
        }

        this.setState(nextState, this.scheduleAutoSave);
    };

    handleRefundRowsChange = (rows: IRefundRow[]) => {
        this.setState(
            { refundRows: this.normalizeClientIds(rows, 'refund'), hasChanges: true },
            this.scheduleAutoSave
        );
    };

    saveReport = async (silent: boolean = false) => {
        if (!this.state.activeReport) return false;

        this.clearAutoSaveTimer();
        if (this.saveInProgress) {
            if (silent) {
                this.autoSaveQueued = true;
            }
            return false;
        }

        const invalidEmailIndex = this.state.paymentContactRows.findIndex(row => {
            const email = String(row.email || '').trim();
            return email !== '' && !this.isValidEmail(email);
        });
        if (invalidEmailIndex !== -1) {
            this.setState({
                activeTab: 'contacts',
                contactSearch: '',
                contactFilter: 'all',
                contactPage: Math.floor(invalidEmailIndex / this.state.contactLimit) + 1,
                saveStatus: 'error',
            });
            if (!silent) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Email non valida',
                    text: 'Controlla l\'indirizzo email del contatto alla riga ' + (invalidEmailIndex + 1) + '.',
                });
            }
            return false;
        }

        const changes = this.buildReportChanges();
        if (!changes || !this.hasPersistableChanges(changes)) {
            this.setState({
                paymentRows: this.state.paymentRows.filter(this.hasPaymentData),
                paymentContactRows: this.state.paymentContactRows.filter(this.hasPaymentContactData),
                refundRows: this.state.refundRows.filter(this.hasRefundData),
                hasChanges: false,
                saveStatus: 'idle',
            });
            return true;
        }

        const reportId = this.state.activeReport.id;
        this.saveInProgress = true;
        this.setState({ isSaving: true, saveStatus: 'saving' });
        if (!silent) {
            EventBus.dispatch("showLoader", { text: 'Salvataggio in corso...' });
        }

        try {
            const result = await PaymentsReportsService.saveChanges(reportId, changes);
            const paymentRows = this.applyChangesResult(this.state.paymentRows, result.payments, this.hasPaymentData);
            const contactRows = this.applyChangesResult(this.state.paymentContactRows, result.contacts, this.hasPaymentContactData);
            const refundRows = this.applyChangesResult(this.state.refundRows, result.refunds, this.hasRefundData);
            let activeReport = { ...this.state.activeReport, version: result.version } as IPaymentReport;
            let synchronizedPaymentRows = paymentRows;
            let synchronizedContactRows = contactRows;
            let synchronizedRefundRows = refundRows;

            if (result.reloadRequired) {
                try {
                    const refreshedReport = await PaymentsReportsService.getReport(reportId);
                    activeReport = refreshedReport;
                    synchronizedPaymentRows = refreshedReport.paymentRows || [];
                    synchronizedContactRows = refreshedReport.contactRows || [];
                    synchronizedRefundRows = refreshedReport.refundRows || [];
                } catch (reloadError) {
                    this.setState({
                        activeReport,
                        paymentRows,
                        paymentContactRows: contactRows,
                        refundRows,
                        savedPaymentRows: paymentRows,
                        savedContactRows: contactRows,
                        savedRefundRows: refundRows,
                        hasChanges: false,
                        saveStatus: 'saved',
                    });
                    this.scheduleSavedStatusReset();
                    await Swal.fire({
                        icon: 'warning',
                        title: 'Salvataggio completato',
                        text: 'Le righe sono state salvate, ma non e stato possibile caricare le modifiche degli altri utenti. Riapri il report per aggiornarlo.',
                    });
                    return true;
                }
            }

            this.setState({
                activeReport,
                paymentRows: synchronizedPaymentRows,
                paymentContactRows: synchronizedContactRows,
                refundRows: synchronizedRefundRows,
                savedPaymentRows: synchronizedPaymentRows,
                savedContactRows: synchronizedContactRows,
                savedRefundRows: synchronizedRefundRows,
                hasChanges: false,
                saveStatus: 'saved',
            });
            this.scheduleSavedStatusReset();
            if (!silent) {
                Swal.fire({ icon: 'success', title: 'Salvato', timer: 1500, showConfirmButton: false });
            }
            return true;
        } catch (error: any) {
            const conflict = error?.response?.status === 409;
            const msg = this.getApiErrorMessage(
                error,
                conflict
                    ? 'Il report e\' stato modificato da un altro utente. Ricaricalo prima di salvare.'
                    : 'Errore nel salvataggio.'
            );
            this.setState({ saveStatus: 'error', hasChanges: true });
            if (!silent || conflict) {
                Swal.fire({
                    icon: conflict ? 'warning' : 'error',
                    title: conflict ? 'Report aggiornato' : 'Errore',
                    text: msg,
                });
            }
            return false;
        } finally {
            if (!silent) {
                EventBus.dispatch("hideLoader");
            }
            this.saveInProgress = false;
            this.setState({ isSaving: false }, () => {
                if (this.autoSaveQueued) {
                    this.autoSaveQueued = false;
                    this.scheduleAutoSave();
                }
            });
        }
    };

    validateReport = async () => {
        if (!this.state.activeReport) return;

        if (this.state.hasChanges) {
            const result = await Swal.fire({
                icon: 'warning',
                title: 'Modifiche non salvate',
                text: 'Salvare prima di validare?',
                showCancelButton: true,
                confirmButtonText: 'Salva e valida',
                cancelButtonText: 'Annulla',
            });
            if (result.isConfirmed) {
                this.setState({ activeTab: 'payments' });
                const saved = await this.saveReport();
                if (!saved) return;
            } else {
                return;
            }
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Conferma validazione',
            text: 'Dopo la validazione il report non potra\' piu\' essere modificato. Continuare?',
            showCancelButton: true,
            confirmButtonText: 'Valida',
            cancelButtonText: 'Annulla',
        });

        if (!result.isConfirmed) return;

        EventBus.dispatch("showLoader", { text: 'Validazione in corso...' });
        try {
            const report = await PaymentsReportsService.validateReport(this.state.activeReport.id);
            this.setState({ activeReport: report, hasChanges: false });
            Swal.fire({ icon: 'success', title: 'Report validato', timer: 1500, showConfirmButton: false });
        } catch (error: any) {
            const msg = error?.response?.data?.error?.description || 'Errore nella validazione.';
            Swal.fire({ icon: 'error', title: 'Errore', text: msg });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    };

    invalidateReport = async () => {
        if (!this.state.activeReport) return;

        const result = await Swal.fire({
            icon: 'warning',
            title: 'Annulla convalida',
            text: 'Il report tornera\' in bozza e potra\' essere modificato dagli utenti autorizzati. I dati presenti non verranno eliminati.',
            showCancelButton: true,
            confirmButtonText: 'Annulla convalida',
            cancelButtonText: 'Mantieni validato',
        });

        if (!result.isConfirmed) return;

        EventBus.dispatch("showLoader", { text: 'Annullamento convalida in corso...' });
        try {
            const report = await PaymentsReportsService.invalidateReport(this.state.activeReport.id);
            await this.loadReports();
            this.setState({ activeReport: report, hasChanges: false });
            Swal.fire({
                icon: 'success',
                title: 'Report riportato in bozza',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error: any) {
            const msg = error?.response?.data?.error?.description || 'Errore durante l\'annullamento della convalida.';
            Swal.fire({ icon: 'error', title: 'Errore', text: msg });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    };

    exportReport = async () => {
        if (!this.state.activeReport) return;
        try {
            const response = await PaymentsReportsService.exportReport(this.state.activeReport.id);
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'REPORT PAGAMENTI RICEVUTI E RIMBORSI EFFETTUATI '
                + this.getMonthName(this.state.activeReport.month).toUpperCase() + ' '
                + this.state.activeReport.year + '.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error: any) {
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile esportare il report.' });
        }
    };

    deleteReport = async () => {
        if (!this.state.activeReport) return;

        const result = await Swal.fire({
            icon: 'warning',
            title: 'Elimina report',
            text: 'Sei sicuro di voler eliminare questo report? L\'operazione e\' irreversibile.',
            showCancelButton: true,
            confirmButtonText: 'Elimina',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#dc3545',
        });

        if (!result.isConfirmed) return;

        EventBus.dispatch("showLoader", { text: 'Eliminazione in corso...' });
        try {
            await PaymentsReportsService.deleteReport(this.state.activeReport.id);
            await this.loadReports();
            this.setState({
                activeReport: null,
                paymentRows: [],
                paymentContactRows: [],
                refundRows: [],
                savedPaymentRows: [],
                savedContactRows: [],
                savedRefundRows: [],
                hasChanges: false,
            });
            Swal.fire({ icon: 'success', title: 'Report eliminato', timer: 1500, showConfirmButton: false });
        } catch (error: any) {
            const msg = error?.response?.data?.error?.description || 'Errore nell\'eliminazione.';
            Swal.fire({ icon: 'error', title: 'Errore', text: msg });
        } finally {
            EventBus.dispatch("hideLoader");
        }
    };

    getMonthName = (m: number): string => {
        const names = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
            'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
        return names[m] || '';
    };

    getStatusLabel = (status: string): string => {
        return status === 'validated' ? 'Validato' : 'Bozza';
    };

    renderSaveStatus = (): React.ReactNode => {
        if (this.isReadOnly()) return null;

        switch (this.state.saveStatus) {
            case 'pending':
                return (
                    <span className="badge badge-warning px-2 py-1">
                        <i className="fa fa-pencil mr-1" /> Modifiche in corso
                    </span>
                );
            case 'saving':
                return (
                    <span className="badge badge-info px-2 py-1">
                        <i className="fa fa-spinner fa-spin mr-1" /> Salvataggio...
                    </span>
                );
            case 'saved':
                return (
                    <span className="badge badge-success px-2 py-1">
                        <i className="fa fa-check mr-1" /> Salvato
                    </span>
                );
            case 'error':
                return (
                    <span className="badge badge-danger px-2 py-1">
                        <i className="fa fa-exclamation-triangle mr-1" /> Salvataggio non riuscito
                    </span>
                );
            default:
                return null;
        }
    };

    render() {
        const { reports, activeReport, activeTab, paymentRows, paymentContactRows, refundRows, hasChanges, isSaving, saveStatus, newMonth, newYear, reportPickerOpen, reportSearch, reportStatusFilter, reportPage, reportTotal, reportPages, contactSearch, contactFilter, contactPage, contactLimit } = this.state;
        const readOnly = this.isReadOnly();

        const totalLordo = paymentRows.reduce((sum, r) => sum + (parseFloat(String(r.grossAmount || 0)) || 0), 0);
        const totalNetto = paymentRows.reduce((sum, r) => sum + (parseFloat(String(r.netAmount || 0)) || 0), 0);
        const totalRimborsi = refundRows.reduce((sum, r) => sum + (parseFloat(String(r.amount || 0)) || 0), 0);
        const paymentReportColumns = paymentColumns.filter(col => !paymentContactColumnKeys.includes(col.key));
        const paymentContactColumns = paymentColumns.filter(col => paymentContactColumnKeys.includes(col.key));
        const filteredContactRows = this.getFilteredContactRows();
        const contactPages = Math.max(1, Math.ceil(filteredContactRows.length / contactLimit));
        const visibleContactRows = this.getVisibleContactRows();
        const contactRowOffset = (contactPage - 1) * contactLimit;

        return (
            <div className="custom-container">
                <div className="card mb-2">
                    <div className="card-body">
                        <div className="form-group d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: '10px' }}>
                            <h2 className="card-title margin-0">Report pagamenti e rimborsi</h2>
                            <div className="d-flex align-items-center flex-wrap margin-0" style={{ gap: '8px' }}>
                                <button
                                    type="button"
                                    className="btn topic-empty-btn-color"
                                    onClick={() => this.setState({ reportPickerOpen: true })}
                                    disabled={isSaving}
                                >
                                    <i className="fa fa-folder-open mr-1" /> {activeReport ? 'Cambia report' : 'Seleziona report'}
                                </button>
                                {activeReport && (
                                    <div className="d-flex align-items-center flex-wrap px-2 py-1 border rounded bg-light" style={{ gap: '8px' }}>
                                        <span className="text-muted">Report</span>
                                        <strong>{this.getMonthName(activeReport.month)} {activeReport.year}</strong>
                                        <span className={'badge ' + (activeReport.status === 'validated' ? 'badge-success' : 'badge-warning')}>
                                            {this.getStatusLabel(activeReport.status)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {activeReport && (
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                <div className="btn-group" role="group" aria-label="Sezioni report">
                                    <button
                                        type="button"
                                        className={'btn ' + (activeTab === 'payments' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ activeTab: 'payments' })}
                                    >
                                        Pagamenti <span style={{ marginLeft: '8px' }} className={'badge ' + (activeTab === 'payments' ? 'badge-light' : 'badge-secondary')}>{paymentRows.length}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={'btn ' + (activeTab === 'contacts' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ activeTab: 'contacts' })}
                                    >
                                        Contatti <span style={{ marginLeft: '8px' }} className={'badge ' + (activeTab === 'contacts' ? 'badge-light' : 'badge-secondary')}>{paymentContactRows.length}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={'btn ' + (activeTab === 'refunds' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ activeTab: 'refunds' })}
                                    >
                                        Rimborsi <span style={{ marginLeft: '8px' }} className={'badge ' + (activeTab === 'refunds' ? 'badge-light' : 'badge-secondary')}>{refundRows.length}</span>
                                    </button>
                                </div>

                            <div className="ml-auto d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {this.renderSaveStatus()}
                                {hasChanges && !readOnly && (
                                    <button className="btn topic-full-btn-color" onClick={() => this.saveReport(false)} disabled={isSaving}>
                                        {isSaving ? <i className="fa fa-spinner fa-spin mr-1" /> : <i className="fa fa-save mr-1" />}
                                        {saveStatus === 'error' ? ' Riprova salvataggio' : ' Salva ora'}
                                    </button>
                                )}
                                {activeReport && this.canValidate() && activeReport.status === 'draft' && (
                                    <button className="btn topic-empty-btn-color" onClick={this.validateReport} disabled={isSaving}>
                                        <i className="fa fa-check mr-1" /> Valida
                                    </button>
                                )}
                                {activeReport && this.canDelete() && activeReport.status === 'validated' && (
                                    <button className="btn topic-empty-btn-color" onClick={this.invalidateReport}>
                                        <i className="fa fa-undo mr-1" /> Annulla convalida
                                    </button>
                                )}
                                {activeReport && this.canExport() && (
                                    <button className="btn topic-empty-btn-color" onClick={this.exportReport}>
                                        <i className="fa fa-download mr-1" /> Excel
                                    </button>
                                )}
                                {activeReport && this.canDelete() && (
                                    <button className="btn btn-outline-danger" onClick={this.deleteReport} title="Elimina report">
                                        <i className="fa fa-trash mr-1" /> Elimina
                                    </button>
                                )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {reportPickerOpen && (
                    <div
                        className="d-flex align-items-start justify-content-center"
                        style={{ position: 'fixed', zIndex: 1050, top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(15, 23, 42, 0.35)', padding: '48px 16px' }}
                        onClick={() => this.setState({ reportPickerOpen: false })}
                    >
                        <div
                            className="card shadow"
                            style={{ width: '720px', maxWidth: '100%', maxHeight: 'calc(100vh - 96px)' }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="card-header py-3 pr-5 position-relative">
                                <div>
                                    <div className="font-weight-bold">Seleziona report pagamenti</div>
                                    <small className="text-muted">{reportTotal} report disponibili</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm text-muted d-flex align-items-center justify-content-center"
                                    style={{ position: 'absolute', top: '8px', right: '8px', width: '36px', height: '36px', padding: 0 }}
                                    aria-label="Chiudi"
                                    title="Chiudi"
                                    onClick={() => this.setState({ reportPickerOpen: false })}
                                >
                                    <i className="fa fa-times" />
                                </button>
                            </div>
                            <div className="card-body p-3" style={{ overflowY: 'auto' }}>
                                {this.canEdit() && (
                                    <div className="border-bottom mb-3 pb-3">
                                        <div className="font-weight-bold mb-2">
                                            <i className="fa fa-plus-circle mr-1" /> Crea nuovo report
                                        </div>
                                        <div className="d-flex align-items-end" style={{ gap: '8px' }}>
                                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                                <label htmlFor="new-report-month" className="small font-weight-bold mb-1">Mese</label>
                                                <select
                                                    id="new-report-month"
                                                    className="form-control form-control-sm"
                                                    value={newMonth}
                                                    onChange={(e) => this.setState({ newMonth: parseInt(e.target.value) })}
                                                >
                                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                                        <option key={m} value={m}>{this.getMonthName(m)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={{ flex: '0 0 112px' }}>
                                                <label htmlFor="new-report-year" className="small font-weight-bold mb-1">Anno</label>
                                                <input
                                                    id="new-report-year"
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={newYear}
                                                    onChange={(e) => this.setState({ newYear: parseInt(e.target.value) })}
                                                />
                                            </div>
                                        </div>
                                        <button className="btn topic-full-btn-color btn-sm w-100 mt-2" onClick={this.createReport} disabled={isSaving}>
                                            <i className="fa fa-plus mr-1" /> Crea report
                                        </button>
                                    </div>
                                )}

                                <label htmlFor="report-search" className="small font-weight-bold mb-1">Cerca nei report</label>
                                <div className="input-group input-group-sm mb-2">
                                    <input
                                        id="report-search"
                                        type="search"
                                        className="form-control"
                                        placeholder="Mese, anno o stato"
                                        value={reportSearch}
                                        onChange={(event) => this.setState({ reportSearch: event.target.value })}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') this.loadReports(1);
                                        }}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            type="button"
                                            className="btn topic-empty-btn-color"
                                            onClick={() => this.loadReports(1)}
                                        >
                                            <i className="fa fa-search mr-1" /> Cerca
                                        </button>
                                    </div>
                                </div>
                                <div className="small font-weight-bold mb-1">Stato</div>
                                <div className="btn-group btn-group-sm w-100 mb-2" role="group" aria-label="Filtro stato report">
                                    <button
                                        type="button"
                                        className={'btn ' + (reportStatusFilter === 'all' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ reportStatusFilter: 'all' }, () => this.loadReports(1))}
                                    >
                                        Tutti
                                    </button>
                                    <button
                                        type="button"
                                        className={'btn ' + (reportStatusFilter === 'draft' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ reportStatusFilter: 'draft' }, () => this.loadReports(1))}
                                    >
                                        Bozze
                                    </button>
                                    <button
                                        type="button"
                                        className={'btn ' + (reportStatusFilter === 'validated' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                        onClick={() => this.setState({ reportStatusFilter: 'validated' }, () => this.loadReports(1))}
                                    >
                                        Validati
                                    </button>
                                </div>
                                <div className="border rounded" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                                    {reports.length === 0 && (
                                        <div className="text-muted text-center py-4 px-2">Nessun report trovato</div>
                                    )}
                                    {reports.map(r => {
                                        const selected = !!activeReport && activeReport.id === r.id;
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                className={'btn btn-block text-left rounded-0 border-0 px-2 py-2 ' + (selected ? 'btn-primary' : 'btn-light')}
                                                onClick={() => this.requestReportChange(r.id)}
                                                disabled={isSaving}
                                            >
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span className="font-weight-bold">{this.getMonthName(r.month)} {r.year}</span>
                                                    <span className={'badge ' + (r.status === 'validated' ? 'badge-success' : 'badge-warning')}>
                                                        {this.getStatusLabel(r.status)}
                                                    </span>
                                                </div>
                                                <small className={selected ? 'text-white-50' : 'text-muted'}>
                                                    {r.status === 'validated' ? 'Pronto per consultazione' : 'In lavorazione'}
                                                </small>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={reportPage <= 1}
                                        onClick={() => this.loadReports(reportPage - 1)}
                                    >
                                        <i className="fa fa-chevron-left" />
                                    </button>
                                    <span className="small text-muted">Pagina {reportPage} di {reportPages}</span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={reportPage >= reportPages}
                                        onClick={() => this.loadReports(reportPage + 1)}
                                    >
                                        <i className="fa fa-chevron-right" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!activeReport ? (
                    <div className="card">
                        <div className="card-body text-center py-5 text-muted">
                            <i className="fa fa-file-text-o" style={{ fontSize: '3rem' }} />
                            <p className="mt-3">Seleziona un report per iniziare la compilazione.</p>
                            <button
                                type="button"
                                className="btn topic-empty-btn-color"
                                onClick={() => this.setState({ reportPickerOpen: true })}
                            >
                                <i className="fa fa-folder-open" /> Apri elenco report
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {activeTab !== 'contacts' && (
                        <div className="card" style={{ overflowX: 'auto' }}>
                            <div className="card-body p-1">
                                <EditableTable
                                    columns={activeTab === 'payments' ? paymentReportColumns : refundColumns}
                                    rows={activeTab === 'payments' ? paymentRows : refundRows}
                                    onRowsChange={activeTab === 'payments'
                                        ? this.handlePaymentRowsChange
                                        : this.handleRefundRowsChange}
                                    readOnly={readOnly}
                                    disabled={isSaving}
                                    tabIndex={0}
                                    addButtonLabel={activeTab === 'payments' ? 'Aggiungi pagamento' : 'Aggiungi rimborso'}
                                    footerContent={this.renderSaveStatus()}
                                />
                            </div>
                        </div>
                        )}

                        {activeTab === 'contacts' && (
                            <div className="card" style={{ overflowX: 'auto' }}>
                                <div className="card-header py-2 d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                        <strong>Contatti e data pagamento</strong>
                                        <span className="badge badge-light">{filteredContactRows.length} di {paymentContactRows.length}</span>
                                    </div>
                                </div>
                                <div className="card-body p-2">
                                    <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
                                        <div className="input-group" style={{ minWidth: '240px', maxWidth: '420px', flex: '1 1 280px' }}>
                                            <div className="input-group-prepend">
                                                <span className="input-group-text"><i className="fa fa-search" /></span>
                                            </div>
                                            <input
                                                type="search"
                                                className="form-control"
                                                placeholder="Cerca nome, email, telefono o data"
                                                value={contactSearch}
                                                onChange={(event) => this.setState({ contactSearch: event.target.value, contactPage: 1 })}
                                            />
                                        </div>
                                        <div className="btn-group" role="group" aria-label="Filtro contatti">
                                            <button
                                                type="button"
                                                className={'btn ' + (contactFilter === 'all' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                                onClick={() => this.setState({ contactFilter: 'all', contactPage: 1 })}
                                            >
                                                Tutti
                                            </button>
                                            <button
                                                type="button"
                                                className={'btn ' + (contactFilter === 'incomplete' ? 'topic-full-btn-color' : 'topic-empty-btn-color')}
                                                onClick={() => this.setState({ contactFilter: 'incomplete', contactPage: 1 })}
                                            >
                                                Incompleti
                                            </button>
                                        </div>
                                    </div>
                                    <EditableTable
                                        columns={paymentContactColumns}
                                        rows={visibleContactRows}
                                        onRowsChange={this.handlePaymentContactRowsChange}
                                        readOnly={readOnly}
                                        disabled={isSaving}
                                        tabIndex={1}
                                        showAddButton={false}
                                        rowIdPrefix="payment-contact-row-"
                                        rowNumberOffset={contactRowOffset}
                                        maxHeight="60vh"
                                    />
                                    <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: '8px' }}>
                                        {!readOnly && (
                                            <button type="button" className="btn topic-empty-btn-color" onClick={this.addPaymentContactRow} disabled={isSaving}>
                                                <i className="fa fa-plus mr-1" /> Aggiungi contatto
                                            </button>
                                        )}
                                        {this.renderSaveStatus()}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center flex-wrap border-top pt-2 mt-2" style={{ gap: '8px' }}>
                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                            <span className="small text-muted">Righe per pagina</span>
                                            <select
                                                className="form-control form-control-sm"
                                                value={contactLimit}
                                                onChange={(event) => this.setState({ contactLimit: parseInt(event.target.value), contactPage: 1 })}
                                                style={{ width: '76px' }}
                                            >
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                        </div>
                                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                            <button
                                                type="button"
                                                className="btn topic-empty-btn-color"
                                                disabled={contactPage <= 1}
                                                onClick={() => this.setState({ contactPage: contactPage - 1 })}
                                                title="Pagina precedente"
                                            >
                                                <i className="fa fa-chevron-left" />
                                            </button>
                                            <span className="small">Pagina {contactPage} di {contactPages}</span>
                                            <button
                                                type="button"
                                                className="btn topic-empty-btn-color"
                                                disabled={contactPage >= contactPages}
                                                onClick={() => this.setState({ contactPage: contactPage + 1 })}
                                                title="Pagina successiva"
                                            >
                                                <i className="fa fa-chevron-right" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={{ fontSize: '0.85rem', padding: '6px 8px', background: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                            <div className="d-flex flex-wrap" style={{ gap: '12px 24px' }}>
                                {activeTab === 'payments' && (
                                    <>
                                        <span><strong>Totale Lordo:</strong> €{totalLordo.toFixed(2)}</span>
                                        <span><strong>Totale Netto:</strong> €{totalNetto.toFixed(2)}</span>
                                    </>
                                )}
                                {activeTab === 'refunds' && (
                                    <span><strong>Totale Rimborsi:</strong> €{totalRimborsi.toFixed(2)}</span>
                                )}
                                {activeTab === 'contacts' && (
                                    <span><strong>Contatti:</strong> {paymentContactRows.length}</span>
                                )}
                            </div>
                            <div className="text-muted mt-1">
                                <span>
                                    {activeTab === 'payments' ? paymentRows.length : activeTab === 'contacts' ? filteredContactRows.length : refundRows.length} righe
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
